import { useCallback, useMemo, useState } from 'react'
import { Button, Spinner } from 'react-bootstrap'
import { FaClipboardList as RelatorioIcon } from 'react-icons/fa'

import { Alert } from '@core/gateways'
import { exportarRelatorioConsumoAlmoco } from '@core/services/restaurante/exportar-relatorio-consumo-almoco'
import { obterRelatorioConsumoAlmoco } from '@core/services/restaurante/relatorio-consumo-almoco'
import { RelatorioConsumoAlmocoItem } from '@core/types/services'
import { TablePage } from '@presentation/components/templates'
import { downloadBlobResponse } from '@presentation/utils/download'
import { FetchProps } from '@presentation/types/hooks'
import { PageProps } from '@presentation/types/components/templates'
import {
  TableColumn,
  TableProps
} from '@presentation/types/components/organisms'

import {
  buildRelatorioConsumoAlmocoRequestParams,
  formatCpf,
  formatDateTime,
  getRelatorioConsumoAlmocoDefaultQueries,
  getRelatorioConsumoAlmocoDefaultValues,
  getRelatorioConsumoAlmocoFilters
} from './utils'

const RelatorioConsumoAlmocoPage: React.FC = () => {
  const defaultQueries = useMemo(
    () => getRelatorioConsumoAlmocoDefaultQueries(),
    []
  )
  const defaultValues = useMemo(
    () => getRelatorioConsumoAlmocoDefaultValues(),
    []
  )

  const [data, setData] = useState<RelatorioConsumoAlmocoItem[]>([])
  const [isFetching, setIsFetching] = useState(true)
  const [totalItems, setTotalItems] = useState(0)
  const [isExporting, setIsExporting] = useState(false)
  const [currentParams, setCurrentParams] =
    useState<Record<string, any>>(defaultQueries)

  const getResponseItems = (response: any): RelatorioConsumoAlmocoItem[] => {
    const data = response?.data ?? response
    const nestedData = data?.data ?? data
    const items = nestedData?.items ?? data?.items ?? []

    return Array.isArray(items) ? items : []
  }

  const getResponseTotal = (
    response: any,
    items: RelatorioConsumoAlmocoItem[]
  ) => {
    const data = response?.data ?? response
    const nestedData = data?.data ?? data

    return (
      nestedData?.pagination?.total ?? data?.pagination?.total ?? items.length
    )
  }

  const fetchData = useCallback(
    async (input?: FetchProps) => {
      setIsFetching(true)

      const params = Object.assign(
        {},
        defaultQueries,
        input?.options,
        input?.queries
      )

      setCurrentParams(params)

      try {
        const response = await obterRelatorioConsumoAlmoco({
          params: buildRelatorioConsumoAlmocoRequestParams(params)
        })
        const items = getResponseItems(response)

        setData(items)
        setTotalItems(getResponseTotal(response, items))
      } catch (error) {
        await Alert.call({
          type: 'error',
          title: 'Falha ao Carregar Relatório',
          description:
            'Tente novamente mais tarde. Caso o problema persista, contate alguém do suporte técnico.'
        })
      } finally {
        setIsFetching(false)
      }
    },
    [defaultQueries]
  )

  const handleExport = useCallback(async () => {
    if (isExporting) return

    setIsExporting(true)

    try {
      const response = await exportarRelatorioConsumoAlmoco({
        params: buildRelatorioConsumoAlmocoRequestParams(currentParams, {
          includePagination: false
        })
      })

      downloadBlobResponse(response, 'relatorio-consumo-almoco.xlsx')

      await Alert.callSuccess({
        title: 'Relatório exportado com sucesso.'
      })
    } catch (error) {
      await Alert.call({
        type: 'error',
        title: 'Falha ao Exportar Relatório',
        description:
          'Não foi possível baixar o arquivo agora. Tente novamente em instantes.'
      })
    } finally {
      setIsExporting(false)
    }
  }, [currentParams, isExporting])

  const pageConfigs: PageProps = {
    icon: <RelatorioIcon />,
    title: 'Relatório de Consumo de Almoço',
    description: 'Página de consulta do consumo de almoços dos alunos.',
    actions: [
      <Button variant="success" onClick={handleExport} disabled={isExporting}>
        {isExporting ? (
          <>
            <Spinner
              as="span"
              animation="border"
              size="sm"
              role="status"
              aria-hidden="true"
              className="me-2"
            />
            Exportando...
          </>
        ) : (
          'Exportar .xlsx'
        )}
      </Button>
    ]
  }

  const filters = getRelatorioConsumoAlmocoFilters()

  const filterConfigs = {
    filters,
    fetchData,
    defaultQueries,
    defaultValues,
    hasStatus: false
  }

  const columns: Array<TableColumn<RelatorioConsumoAlmocoItem>> = [
    {
      name: 'Data/Hora',
      selector: item => formatDateTime(item.data_horario_consumo),
      center: true,
      grow: 0.9,
      sortable: true,
      sortField: 'data_horario_consumo'
    },
    {
      name: 'Nome',
      selector: item => item.nome ?? '',
      center: true,
      grow: 2.2,
      sortable: true,
      sortField: 'nome'
    },
    {
      name: 'Matrícula',
      selector: item => item.matricula ?? '',
      center: true,
      grow: 0.9,
      sortable: true,
      sortField: 'matricula'
    },
    {
      name: 'CPF',
      selector: item => formatCpf(item.cpf),
      center: true,
      grow: 0.9,
      sortable: true,
      sortField: 'cpf'
    },
    {
      name: 'Curso',
      selector: item => item.curso ?? '',
      center: true,
      grow: 2.8,
      sortable: true,
      sortField: 'curso'
    },
    {
      name: 'Turno',
      selector: item => item.turno_curso ?? '',
      center: true,
      sortable: true,
      sortField: 'turno_curso'
    },
    {
      name: 'Tipo de consumo',
      selector: item => item.tipo_consumo ?? '',
      center: true,
      grow: 1.7,
      sortable: true,
      sortField: 'tipo_consumo'
    }
  ]

  const tableConfigs: TableProps = {
    columns,
    data,
    apiConfigs: {
      fetchData,
      isFetching,
      totalItems
    }
  }

  return (
    <TablePage
      pageConfigs={pageConfigs}
      filterConfigs={filterConfigs}
      tableConfigs={tableConfigs}
    />
  )
}

export default RelatorioConsumoAlmocoPage
